'use client'

import { useEffect, useState, useCallback } from 'react'
import type { ServiceLabels } from '@/lib/form-state/util'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdCheck,
    MdClose,
    MdError,
    MdNewReleases,
    MdUpdate,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import {
    getImportPending,
    confirmImportPending,
    cancelImportPending,
    ImportPendingRecord,
    ImportPendingResponse,
    ConfirmImportResult,
    ImportItemType,
} from '@/lib/api/share/share-api'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'

/** Rótulos no vocabulário do segmento (fora da saúde, Procedimento vira Pedido). */
function buildModuleLabels(labels: ServiceLabels): Record<string, string> {
    return {
        order_catalog: labels.Orders,
        exam_catalog: labels.Exams,
        consultation_catalog: labels.Consultations,
    }
}

function buildItemTypeLabels(
    labels: ServiceLabels
): Record<ImportItemType, string> {
    return {
        PROCEDURE: labels.Order,
        CONSULTATION: labels.Consultation,
        EXAM: labels.Exam,
    }
}

const ITEM_TYPE_BADGE_CLASS: Record<ImportItemType, string> = {
    PROCEDURE: 'badge-secondary',
    CONSULTATION: 'badge-info',
    EXAM: 'badge-accent',
}

function centsToReais(cents: number | null | undefined): string {
    if (cents == null || isNaN(cents)) return ''
    return (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function reaisToCents(value: string): number {
    const cleaned = value.replace(',', '.')
    const num = parseFloat(cleaned)
    if (isNaN(num)) return 0
    return Math.round(num * 100)
}

export default function ImportReviewPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()

    const [loading, setLoading] = useState(true)
    const [pending, setPending] = useState<ImportPendingResponse | null>(null)
    const [records, setRecords] = useState<ImportPendingRecord[]>([])
    const [confirming, setConfirming] = useState(false)
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [cancelling, setCancelling] = useState(false)
    const [result, setResult] = useState<ConfirmImportResult | null>(null)

    const loadPending = useCallback(async () => {
        if (!id) return
        setLoading(true)
        try {
            const res = await getImportPending(parseInt(id))
            if (res.success && res.result) {
                setPending(res.result)
                setRecords(res.result.records as ImportPendingRecord[])
            } else {
                toast.error(res.error || 'Erro ao carregar importação pendente')
                navigate(-1)
            }
        } catch {
            toast.error('Erro ao carregar importação pendente')
            navigate(-1)
        } finally {
            setLoading(false)
        }
    }, [id, navigate])

    useEffect(() => {
        loadPending()
    }, [loadPending])

    const isOrderCatalog = pending?.module === 'order_catalog'
    // Coluna e célula somem juntas, senão a tabela desalinha.
    const serviceLabels = useServiceLabel()
    const { isHealth } = serviceLabels
    const moduleLabels = buildModuleLabels(serviceLabels)
    const itemTypeLabels = buildItemTypeLabels(serviceLabels)

    const updateRecord = (index: number, field: string, value: any) => {
        setRecords(prev => {
            const updated = [...prev]
            const record = { ...updated[index] }

            if (field === 'itemType') {
                record.itemType = value as ImportItemType
                updated[index] = record
                return updated
            }

            if (field === 'price') {
                const cents = reaisToCents(value)
                record.price = cents
                if (record.markupPercentage != null && record.markupPercentage > 0 && cents > 0) {
                    record.sellingPrice = Math.round(cents * (1 + record.markupPercentage / 100))
                }
            } else if (field === 'markupPercentage') {
                const markup = value === '' ? null : parseFloat(value.replace(',', '.'))
                record.markupPercentage = markup
                if (markup != null && markup > 0 && record.price > 0) {
                    record.sellingPrice = Math.round(record.price * (1 + markup / 100))
                }
            } else if (field === 'sellingPrice') {
                const cents = reaisToCents(value)
                record.sellingPrice = cents
                if (record.price > 0 && cents > 0) {
                    record.markupPercentage = parseFloat((((cents / record.price) - 1) * 100).toFixed(2))
                }
            }

            updated[index] = record
            return updated
        })
    }

    const handleConfirm = async () => {
        if (!id) return
        setConfirming(true)
        try {
            const res = await confirmImportPending(parseInt(id), records)
            if (res.success && res.result) {
                setResult(res.result)
                toast.success('Importação confirmada com sucesso!')
            } else {
                toast.error(res.error || 'Erro ao confirmar importação')
            }
        } catch {
            toast.error('Erro ao confirmar importação')
        } finally {
            setConfirming(false)
        }
    }

    const handleCancel = async () => {
        if (!id) return
        setCancelling(true)
        try {
            await cancelImportPending(parseInt(id))
            toast.info('Importação cancelada')
            navigate(-1)
        } catch {
            toast.error('Erro ao cancelar importação')
        } finally {
            setCancelling(false)
            setShowCancelModal(false)
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <span className="loading loading-spinner loading-lg text-secondary" />
            </div>
        )
    }

    if (!pending) {
        return null
    }

    if (result) {
        const hasErrors = result.errors.length > 0
        return (
            <div className="p-6 max-w-4xl mx-auto">
                <h1 className="text-2xl font-bold mb-6">Resultado da Importação</h1>

                <div className={`alert ${hasErrors ? 'alert-warning' : 'alert-success'} mb-6`}>
                    {hasErrors ? <MdError className="text-xl" /> : <MdCheck className="text-xl" />}
                    <div>
                        <p className="font-medium">
                            {result.created > 0 && `${result.created} criados`}
                            {result.created > 0 && result.updated > 0 && ', '}
                            {result.updated > 0 && `${result.updated} atualizados`}
                            {hasErrors && `, ${result.errors.length} erros`}
                        </p>
                    </div>
                </div>

                {hasErrors && (
                    <div className="space-y-2 mb-6">
                        {result.errors.map((err, idx) => (
                            <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-error/10">
                                <MdError className="text-error text-lg flex-shrink-0" />
                                <p className="text-sm text-error">
                                    Item {err.index + 1}: {err.error}
                                </p>
                            </div>
                        ))}
                    </div>
                )}

                <button
                    className="btn btn-secondary"
                    onClick={() => navigate(-1)}
                >
                    Voltar
                </button>
            </div>
        )
    }

    const moduleLabel = moduleLabels[pending.module] || pending.module
    const expiresIn = formatDistanceToNow(new Date(pending.expiresAt), {
        addSuffix: true,
        locale: ptBR,
    })

    return (
        <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
                <button
                    className="btn btn-ghost btn-sm btn-circle"
                    onClick={() => navigate(-1)}
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex-1">
                    <h1 className="text-2xl font-bold">Revisão de Importação</h1>
                    <p className="text-sm text-base-content/60">
                        {moduleLabel} - Parceiro: {pending.partnerName} - {pending.totalRecords} itens - Expira {expiresIn}
                    </p>
                </div>
            </div>

            <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-200">
                <table className="table table-sm">
                    <thead>
                        <tr className="bg-base-200/50">
                            <th className="w-20">Status</th>
                            {isOrderCatalog && <th className="w-36">Classificação</th>}
                            <th>Nome</th>
                            {isHealth && <th className="w-28">Cód. TUSS</th>}
                            <th className="w-28">Tipo</th>
                            <th className="w-36">Preço (R$)</th>
                            <th className="w-28">Markup (%)</th>
                            <th className="w-36">Preço de Venda (R$)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {records.map((record, index) => (
                            <tr key={index} className="hover">
                                <td>
                                    {record.existsInDb ? (
                                        <span className="badge badge-warning badge-sm gap-1">
                                            <MdUpdate className="text-xs" />
                                            Existente
                                        </span>
                                    ) : (
                                        <span className="badge badge-success badge-sm gap-1">
                                            <MdNewReleases className="text-xs" />
                                            Novo
                                        </span>
                                    )}
                                </td>
                                {isOrderCatalog && (
                                    <td>
                                        <select
                                            className="select select-bordered select-sm w-full"
                                            value={record.itemType || 'PROCEDURE'}
                                            onChange={e => updateRecord(index, 'itemType', e.target.value)}
                                        >
                                            {(Object.entries(itemTypeLabels) as [ImportItemType, string][]).map(
                                                ([value, label]) => (
                                                    <option key={value} value={value}>
                                                        {label}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </td>
                                )}
                                <td>
                                    <span className="font-medium text-sm">{record.name}</span>
                                    {record.description && (
                                        <p className="text-xs text-base-content/50 truncate max-w-xs">
                                            {record.description}
                                        </p>
                                    )}
                                </td>
                                {isHealth && (
                                    <td className="text-sm text-base-content/70">
                                        {record.tussCode || '-'}
                                    </td>
                                )}
                                <td className="text-sm text-base-content/70">
                                    {record.type || '-'}
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm w-full"
                                        value={centsToReais(record.price)}
                                        onChange={e => updateRecord(index, 'price', e.target.value)}
                                        placeholder="0,00"
                                    />
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm w-full"
                                        value={record.markupPercentage != null ? String(record.markupPercentage) : ''}
                                        onChange={e => updateRecord(index, 'markupPercentage', e.target.value)}
                                        placeholder="0"
                                    />
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm w-full"
                                        value={centsToReais(record.sellingPrice)}
                                        onChange={e => updateRecord(index, 'sellingPrice', e.target.value)}
                                        placeholder="0,00"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between mt-6">
                <button
                    className="btn btn-ghost gap-2"
                    onClick={() => setShowCancelModal(true)}
                    disabled={confirming}
                >
                    <MdClose className="text-lg" />
                    Cancelar Importação
                </button>

                <button
                    className="btn btn-secondary gap-2"
                    onClick={handleConfirm}
                    disabled={confirming}
                >
                    {confirming ? (
                        <span className="loading loading-spinner loading-sm" />
                    ) : (
                        <MdCheck className="text-lg" />
                    )}
                    Confirmar Importação ({records.length} itens)
                </button>
            </div>

            <ConfirmModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={handleCancel}
                title="Cancelar Importação"
                message="Tem certeza que deseja cancelar esta importação? Os dados extraídos serão descartados."
                loading={cancelling}
                confirmButtonText="Sim, cancelar"
                cancelButtonText="Voltar"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
